import { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { css } from '@emotion/core';

// redux
import { ComponentType } from 'redux/schemas/models/lecture-component';
import { QuizQuestionType } from 'redux/schemas/models/quiz';
import { getFlatCourseAliases } from 'redux/selectors/course';
import { getComponentActivity, getQuizQuestionResponse } from 'redux/selectors/quizzes';

import useQuestionSetHelpers from 'quizzes/hooks/use-question-set-helpers';
import ClickableContainer, { ClickableContainerProps } from 'components/clickable-container';
import QuestionSetContext from 'lecture_pages/services/question-set-context';
import {
  black,
  danger,
  gray1,
  gray3,
  gray4,
  gray6,
  primary,
  success,
  warning,
  white,
} from 'styles/global_defaults/colors';
import { largeSpacing, quarterSpacing } from 'styles/global_defaults/scaffolding';
import NvTooltip from 'shared/components/nv-tooltip';
import NvIcon from 'shared/components/nv-icon';
import t from 'react-translate';
import { getItemBaseStyles } from '.';
import LearnerNavigationContent from './learner-navigation-content';


const TOOLTIP_ITEM_SIZE = 36;
const TOOLTIP_ARROW_SIZE = 4;

export type QuestionNavigatorItemProps = {
  index: number;
  active: boolean;
  className?: string;
  onClick?: ClickableContainerProps['onClick'];
  onDuplicate?: () => void;
  onDelete?: () => void;
  disabled?: boolean;
  questionId?: number;
  isDragOverlay?: boolean;
  disableDragging?: boolean;
};

const QuestionNavigatorItem = (props: QuestionNavigatorItemProps) => {
  const {
    index,
    active,
    onClick,
    onDelete,
    className,
    onDuplicate,
    disabled = false,
    questionId,
    isDragOverlay,
    disableDragging,
  } = props;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: questionId, data: { index } });

  const {
    isEditMode,
    isReviewMode,
    isAnswerMode,
    dataQaQuestionSetAttributes,
  } = useQuestionSetHelpers();

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const [showTooltip, setShowTooltip] = useState(false);
  const courseAliases = useSelector(getFlatCourseAliases);

  const {
    lectureComponent,
    questions,
    canMakeStructuralChanges,
  } = useContext(QuestionSetContext);
  const questionResponse = useSelector((state) => getQuizQuestionResponse(state, (isReviewMode ? questions[index]?.previousAttemptResponses : questions[index]?.responses)));
  const activity = useSelector((state) => getComponentActivity(state, lectureComponent));

  useEffect(() => {
    if (isDragging) {
      setShowTooltip(false);
    }
  }, [isDragging]);

  const currentQuestion = questions[index];
  const questionIsStatement = currentQuestion?.type === QuizQuestionType.STATEMENT;
  const showLearnerNavigation = !isEditMode && (!showTooltip || questionIsStatement);

  const isCorrect = questionResponse?.isCorrect;
  const isWrong = (() => {
    const isIncorrect = (questionResponse && !questionResponse.isCorrect);
    const exhaustAttempts = questionResponse?.completedQuestionAttempts >= activity?.questionMaximumAttempts;

    // Active in answer mode, considering wrong when user exhaust all the attempts with incorrect reponse
    return (isAnswerMode && active) ? (isIncorrect && exhaustAttempts) : isIncorrect;
  })();

  const hasValidResponse = isCorrect || isWrong;
  const { fontColor, backgroundColor } = (() => {
    if (!questionIsStatement
      && (
        // If showing the learner navigation and has a considered user response.
        (showLearnerNavigation && hasValidResponse)
        // For handling the hover state color.
        || (!isEditMode && hasValidResponse && showTooltip)
      )
    ) {
      return {
        fontColor: questionResponse?.isCorrect ? success : danger,
        backgroundColor: 'transparent',
      };
    }

    return {
      fontColor: active ? white : gray1,
      backgroundColor: active ? primary : 'transparent',
    };
  })();

  const styles = css`
    color: ${fontColor};
    &:focus-within {
      .tooltip-container {
        display: block !important;
      }
    }
    // Overriding the border color for the statement.
    ${getItemBaseStyles(active, disabled, (questionIsStatement && !isEditMode) ? white : fontColor)};
    background-color: ${backgroundColor};
    ${active && css`
      outline-color: ${warning}!important;
    `};

    ${active && !isEditMode && questionResponse && css`
      border: 1px solid ${fontColor};
    `};

    ${!isAnswerMode && css`
      &[aria-disabled='true'] {
        color: ${gray4};
      }
    `};

    height: ${largeSpacing}px;
    min-width: ${largeSpacing}px;
  `;

  const tooltipStyles = css`
    display: ${(showTooltip && !isDragging) ? 'block' : 'none'};
    padding-bottom: ${TOOLTIP_ARROW_SIZE}px;
    top: -${TOOLTIP_ITEM_SIZE + TOOLTIP_ARROW_SIZE}px;

    &:before {
      left: 50%;
      bottom: 0;
      content: "";
      position: absolute;
      border-style: solid;
      border-color: transparent;
      border-top-color: ${black};
      transform: translateX(-50%);
      border-width: ${TOOLTIP_ARROW_SIZE}px ${TOOLTIP_ARROW_SIZE}px 0;
    }

    .tooltip-body {
      color: ${white};
      background-color: ${black};
      border-radius: ${quarterSpacing}px;

      .tooltip-option {
        width: ${TOOLTIP_ITEM_SIZE}px;
        height: ${TOOLTIP_ITEM_SIZE}px;

        &:not([aria-disabled=true]):hover {
          background-color: ${gray3};
        }
      }
    }
  `;

  const isDeleteQuestionDisabled = questions.length === 1;

  const dropPlaceHolderStyle = css`
    min-height: ${largeSpacing}px;
    min-width: ${largeSpacing}px;
    background-color: ${gray6};
    border-radius: 6px;
  `;

  if (isDragging && !isDragOverlay && !disableDragging) {
    return (
      <div ref={setNodeRef} css={dropPlaceHolderStyle} style={style} />
    );
  }

  const getDisplayNumber = () => (isEditMode ? index + 1 : currentQuestion.displayIndex);

  const getReleasedLessonDeleteQuestionDisabledText = () => {
    switch (lectureComponent.type) {
      case ComponentType.QUIZ:
      case ComponentType.PROGRESSIVE_QUIZ:
        return t.QUIZZES.ALREADY_RELEASED.QUESTION_DELETE(courseAliases);
      case ComponentType.SURVEY:
        return t.SURVEYS.ALREADY_RELEASED.QUESTION_DELETE(courseAliases);
      default:
        return '';
    }
  };

  const getReleasedLessonDuplicateQuestionDisabledText = () => {
    switch (lectureComponent.type) {
      case ComponentType.QUIZ:
      case ComponentType.PROGRESSIVE_QUIZ:
        return t.QUIZZES.ALREADY_RELEASED.DUPLICATE(courseAliases);
      case ComponentType.SURVEY:
        return t.SURVEYS.ALREADY_RELEASED.DUPLICATE(courseAliases);
      default:
        return '';
    }
  };

  return (
    <ClickableContainer
      css={styles}
      onMouseOver={() => setShowTooltip(true)}
      onMouseOut={() => setShowTooltip(false)}
      onClick={onClick}
      key={questionId}
      ref={setNodeRef}
      {...(isEditMode ? attributes : {})}
      {...listeners}
      style={style}
      disabled={disabled}
      aria-label={questionIsStatement ? t.QUIZZES.PROGRESSIVE_QUIZ_BASICS.STATEMENT() : t.QUIZZES.QUESTION_NUMBER(currentQuestion.displayIndex)}
      className={`d-flex align-items-center justify-content-center ${className} label position-relative`}
      data-qa={dataQaQuestionSetAttributes.questionModal.questionNavigatorButton}
      data-qa-id={`${dataQaQuestionSetAttributes.questionModal.questionNavigatorButton}_${index + 1}`}
    >
      {showLearnerNavigation
        ? (
          <LearnerNavigationContent
            index={index}
            active={active}
            isCorrect={isCorrect}
            isWrong={isWrong}
            disabled={disabled}
          />
        ) : (getDisplayNumber())}
      {isEditMode && (
      <div
        css={tooltipStyles}
        className='position-absolute tooltip-container'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='d-flex tooltip-body flex-row pl-1 pr-1'>
          <NvTooltip text={!canMakeStructuralChanges ? getReleasedLessonDuplicateQuestionDisabledText() : t.QUIZZES.DUPLICATE_QUESTION()} preventOverflow={false}>
            <ClickableContainer
              onClick={onDuplicate}
              disabled={!canMakeStructuralChanges}
              className='tooltip-option p-2'
              data-qa={dataQaQuestionSetAttributes.questionModal.questionNavigatorButtonDuplicate}
              data-qa-id={`${dataQaQuestionSetAttributes.questionModal.questionNavigatorButtonDuplicate}_${index + 1}`}
            >
              <NvIcon icon='duplicate' size='smallest' className={!canMakeStructuralChanges ? 'gray-3' : undefined} />
            </ClickableContainer>
          </NvTooltip>
          {!isDeleteQuestionDisabled && (
            <NvTooltip text={!canMakeStructuralChanges ? getReleasedLessonDeleteQuestionDisabledText() : t.QUIZZES.DELETE_QUESTION()} preventOverflow={false}>
              <ClickableContainer
                onClick={onDelete}
                disabled={!canMakeStructuralChanges}
                className='tooltip-option p-2'
                data-qa={dataQaQuestionSetAttributes.questionModal.questionNavigatorButtonDelete}
                data-qa-id={`${dataQaQuestionSetAttributes.questionModal.questionNavigatorButtonDelete}_${index + 1}`}
              >
                <NvIcon icon='trash' size='smallest' className={!canMakeStructuralChanges ? 'gray-3' : undefined} />
              </ClickableContainer>
            </NvTooltip>
          )}
        </div>
      </div>
      )}
    </ClickableContainer>
  );
};

export default QuestionNavigatorItem;
